import { useNavigation } from '@react-navigation/native'
import { StackScreenProps } from '@react-navigation/stack'
import * as Clipboard from 'expo-clipboard'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { Icon, Text } from 'react-native-paper'
import Toast from 'react-native-root-toast'
import { useAuthContext } from '../../contexts/auth-context'
import { EmptyList } from '../../@ui/containers/lists/empty-list'
import LoadingFullScreen from '../../@ui/containers/grid/full-screen.spinner'
import Row from '../../@ui/containers/grid/row'
import { ScreenWrapper } from '../../@ui/containers/grid/screen.wrapper'
import colors from '../../constants/colors'
import { createStyle } from '../../contexts/global-styles.provider'
import { useGetTaskQuery } from '../../generated/graphql'
import { useWechatShareInit } from '../../hooks/wechat-share.hook'
import { AppNavigation, AppNavigatorParams } from '../../navigation/types'
import { TouchableIconWithToolTip } from '../../utils/meldd-tooltip'
import { UserLink } from '../../@ui/typo/user.link'
import { TaskActions } from './components/task-actions'
import { TaskContributorsTabs } from './components/task-contributors-tabs'
import { TaskStatus } from './components/task-status'
import { TaskTypeButton } from './components/task-type.button'
import {
  MelddTasks,
  navigateToTaskTarget,
  useGetTarget,
  getTaskUrl,
} from './task.types'
import { MelddCard } from '../../@ui/containers/cards/meldd.card'
import moment from 'moment'
const TaskHeader = ({ task }: { task: MelddTasks }) => {
  const styles = useStyle()
  const navigation = useNavigation<AppNavigation>()
  const { t } = useTranslation('tasks')

  const copyUrl = async () => {
    const url = getTaskUrl(task)
    await Clipboard.setStringAsync(url)
    Toast.show(t('copied'))
  }

  const parsedDate = moment(task.createdAt).format('YYYY-MM-DD')
  return (
    <View style={{ flex: 1, margin: 20 }}>
      <Text variant={'titleLarge'}>{task.title}</Text>

      <Text variant={'bodyMedium'} style={{ marginTop: 20 }}>
        {t('task-url')}
      </Text>
      <Row alignItems={'center'} columnGap={8} wrap style={{ marginTop: 8 }}>
        <View style={{ flex: 1 }}>
          <Text variant={'bodySmall'} style={styles.urlText}>
            {getTaskUrl(task)}
          </Text>
        </View>
        <TouchableIconWithToolTip
          icon={<Icon source="content-copy" size={18} />}
          tooltip={t('copy')}
          mode="text"
          onPress={copyUrl}
        />
      </Row>

      <Text variant={'bodyMedium'} style={{ marginTop: 20 }}>
        {t('task')}
      </Text>
      <Row justifyContent={'space-between'} wrap>
        <Row alignItems={'center'} columnGap={2}>
          <TaskTypeButton task={task} />
          <Text>
            {' - '}
            {t(`type.${task.type}`)}
          </Text>
        </Row>

        <Row columnGap={4}>
          <TouchableIconWithToolTip
            icon={<Icon source="eye-outline" size={18} />}
            tooltip={t('view')}
            mode="text"
            onPress={() => navigateToTaskTarget({ task, navigation })}
          />
        </Row>
      </Row>

      <Text variant={'bodyMedium'} style={{ marginTop: 20 }}>
        {t('author')}
      </Text>
      <Row flex={1} columnGap={8} wrap>
        <Row alignItems={'center'} style={styles.subtitle} columnGap={2}>
          {task.owner && (
            <>
              <UserLink user={task.owner} />
              <Text>{' •'}</Text>
            </>
          )}
          <Text>{parsedDate}</Text>
        </Row>
      </Row>
    </View>
  )
}

const TaskContributions = ({ task }: { task: MelddTasks }) => {
  const { userId } = useAuthContext()
  const isOwner = task.owner.id === userId

  if (isOwner) {
    return <TaskContributorsTabs task={task} isOwner={isOwner} />
  }

  return null
}
export function TaskDetail({
  route,
}: StackScreenProps<AppNavigatorParams, 'Task'>) {
  const taskId = route.params.taskId || ''
  const { t } = useTranslation('tasks')
  const { loading, data } = useGetTaskQuery({
    variables: {
      taskId,
    },
    skip: taskId === '',
  })

  const task = data?.getTask

  useWechatShareInit(() => {
    if (!task) return null
    return {
      title: task.title,
      description: `${t(`type.${task.type}`)}!`,
    }
  }, [task])

  if (loading) {
    return <LoadingFullScreen />
  }

  if (!task) {
    return <EmptyList message={t('task-not-found')} />
  }

  return (
    <ScreenWrapper>
      <View style={{ flex: 1, margin: 20 }}>
        <MelddCard header={<TaskHeader task={task} />}>
          <TaskActions task={task} />
          <View style={{ margin: 4 }} />
          {!task.assignedId && <TaskContributions task={task} />}
          {/* // TODO: When having assignees show the assignee information here */}
        </MelddCard>
      </View>
    </ScreenWrapper>
  )
}

const useStyle = createStyle(({ theme }) => ({
  contributeButton: {
    marginTop: theme.spacing(2),
  },
  contributeListItem: {
    paddingVertical: theme.spacing(2),
    paddingHorizontal: theme.spacing(4),
  },

  backButton: {
    marginRight: theme.spacing(2),
  },
  subtitle: {
    fontSize: 14,
    color: colors.grey600,
    marginTop: theme.spacing(1),
  },
  centerContainer: {
    flex: 1,
    marginVertical: theme.spacing(6),
    justifyContent: 'center',
    alignItems: 'center',
  },
  urlText: {
    color: colors.grey600,
    fontSize: 12,
  },
}))

export default TaskDetail
